/**
 * Shared decode-aware radar layer transition helper.
 *
 * Uses two Mapbox image sources + raster layers (A/B) and the strategy:
 *   "old stays visible, new fades in on top, old hides after completion."
 *
 * Key guarantees:
 *   - The fade does NOT start until Mapbox has loaded/rendered the new URL
 *     (polled via isSourceLoaded + rAF, hard timeout fallback).
 *   - A monotonic generation token gates every async callback so only the
 *     latest transition may touch opacity or flip the active slot.
 *   - On any cancellation, error, or style-not-loaded during a fade the
 *     layers are snapped to a deterministic state instead of being
 *     abandoned at partial opacity.
 *   - Init/reset normalizes both slots explicitly, handling existing-but-
 *     stale layers from a previous lifecycle.
 *
 * Timing budget:
 *   - At high fps (≥ 4): instant swap after source readiness (no fade).
 *   - At low fps (1-3): short fade (40-80 ms), with source-wait capped
 *     so wait + fade fits within ~80% of the frame interval.
 *   - On normal next-frame cancellation: if the source was already loaded
 *     the transition completes *forward* (new frame snaps to full, old
 *     hides, slot flips) instead of snapping *backward* to the old frame.
 *     This prevents visible backward jumps at 5 fps.
 */
import type { Map as MapboxMap } from 'mapbox-gl';

// ============================================================================
// Types
// ============================================================================

/** Minimal cache interface -- satisfied by both Map and LRUImageCache. */
export interface ImageCacheLike {
  get(key: string): HTMLImageElement | undefined;
  set(key: string, value: HTMLImageElement): void;
  has?(key: string): boolean;
}

type CoordsTuple = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

export interface TransitionLayerConfig {
  sourceIdA: string;
  layerIdA: string;
  sourceIdB: string;
  layerIdB: string;
}

export interface TransitionState {
  activeSlot: 'A' | 'B';
  /** Monotonically increasing -- each call to `transitionToFrame` bumps it. */
  generation: number;
  /** Current rAF id (poll-wait OR fade animation). */
  fadeRafId: number | null;
  initialized: boolean;
  /**
   * Set to true once `isSourceLoaded()` confirms the inactive source
   * has rendered the new URL.  Used by the cancellation handler to
   * decide whether to complete forward or snap backward.
   */
  sourceReady: boolean;
}

// ============================================================================
// Factory
// ============================================================================

export function createTransitionState(): TransitionState {
  return {
    activeSlot: 'A',
    generation: 0,
    fadeRafId: null,
    initialized: false,
    sourceReady: false,
  };
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Find the first layer above both radar layers that is NOT one of the two
 * radar layers.  Used as the `beforeId` anchor so `moveLayer` keeps radar
 * below all overlays instead of pushing it to the absolute top of the stack.
 */
function findOverlayAnchor(
  map: MapboxMap,
  config: TransitionLayerConfig,
): string | undefined {
  try {
    const layers = map.getStyle()?.layers;
    if (!layers) return undefined;

    const radarIds = new Set([config.layerIdA, config.layerIdB]);
    let lastRadarIdx = -1;
    for (let i = 0; i < layers.length; i++) {
      if (radarIds.has(layers[i].id)) lastRadarIdx = i;
    }
    if (lastRadarIdx === -1 || lastRadarIdx + 1 >= layers.length) {
      return undefined;
    }
    return layers[lastRadarIdx + 1].id;
  } catch {
    return undefined;
  }
}

/**
 * Create-or-update a source + layer to a fully known state.
 * Handles all four combos (source exists/not, layer exists/not) so callers
 * never need to worry about stale URLs, wrong opacity, or invisible layers.
 */
function normalizeSlot(
  map: MapboxMap,
  sourceId: string,
  layerId: string,
  url: string,
  coordinates: CoordsTuple,
  opacity: number,
): void {
  try {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'image', url, coordinates });
    } else {
      const src = map.getSource(sourceId) as
        | { updateImage?: (o: { url: string; coordinates: CoordsTuple }) => void }
        | undefined;
      src?.updateImage?.({ url, coordinates });
    }

    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: { 'raster-opacity': opacity, 'raster-fade-duration': 0 },
      });
    } else {
      map.setPaintProperty(layerId, 'raster-opacity', opacity);
      map.setPaintProperty(layerId, 'raster-fade-duration', 0);
      map.setLayoutProperty(layerId, 'visibility', 'visible');
    }
  } catch {
    // map/style invalid
  }
}

/**
 * Snap both layers to a deterministic safe state: active at full opacity,
 * inactive at 0.  Called when a transition is cancelled before the source
 * was confirmed ready (the inactive layer is still at 0, so no visual
 * artefact) or on hard errors.
 */
function snapToSafeState(
  map: MapboxMap,
  config: TransitionLayerConfig,
  state: TransitionState,
  opacity: number,
): void {
  try {
    const activeLayerId =
      state.activeSlot === 'A' ? config.layerIdA : config.layerIdB;
    const inactiveLayerId =
      state.activeSlot === 'A' ? config.layerIdB : config.layerIdA;
    if (map.getLayer(activeLayerId)) {
      map.setPaintProperty(activeLayerId, 'raster-opacity', opacity);
    }
    if (map.getLayer(inactiveLayerId)) {
      map.setPaintProperty(inactiveLayerId, 'raster-opacity', 0);
    }
  } catch {
    // ignore -- map may be destroyed
  }
  state.fadeRafId = null;
  state.sourceReady = false;
}

/**
 * Guarded swap: set the incoming layer to full opacity, hide the outgoing
 * layer, and flip `activeSlot`.  Both layers must exist and both paint
 * writes must succeed; otherwise fall back to `snapToSafeState` so the
 * transition state stays consistent with what is actually visible.
 *
 * This is the **single path** through which `activeSlot` may be flipped.
 * All completion sites (cancellation-forward, instant swap, fade finish)
 * call this instead of manipulating `activeSlot` directly.
 */
function commitSwap(
  map: MapboxMap,
  config: TransitionLayerConfig,
  state: TransitionState,
  opacity: number,
): void {
  const activeLayerId =
    state.activeSlot === 'A' ? config.layerIdA : config.layerIdB;
  const inactiveLayerId =
    state.activeSlot === 'A' ? config.layerIdB : config.layerIdA;

  try {
    if (!map.getLayer(inactiveLayerId) || !map.getLayer(activeLayerId)) {
      snapToSafeState(map, config, state, opacity);
      return;
    }
    map.setPaintProperty(inactiveLayerId, 'raster-opacity', opacity);
    map.setPaintProperty(activeLayerId, 'raster-opacity', 0);
  } catch {
    snapToSafeState(map, config, state, opacity);
    return;
  }

  state.activeSlot = state.activeSlot === 'A' ? 'B' : 'A';
  state.fadeRafId = null;
  state.sourceReady = false;
}

// ============================================================================
// Timing helpers
// ============================================================================

/**
 * Compute adaptive fade duration and source-wait timeout so that the full
 * transition (wait + fade) fits within ~80% of the frame interval.
 *
 * At high fps (≥ 4):  instant swap (fadeDuration = 0).
 * At low fps (1-3):   short fade (40-80 ms).
 */
function computeTimingBudget(fps: number) {
  const cappedFps = Math.min(fps, 5);
  const frameDelay = 1000 / cappedFps;

  // No fade at high fps -- avoids double-exposure shimmer and guarantees
  // the transition completes well within the 200-250 ms frame.
  const fadeDuration =
    cappedFps >= 4
      ? 0
      : Math.max(40, Math.min(80, frameDelay * 0.3));

  // Source-wait timeout: leave room for the fade and a safety margin.
  const maxSourceWait = Math.min(300, Math.max(60, frameDelay * 0.6 - fadeDuration));

  return { fadeDuration, maxSourceWait };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Transition to a new radar frame.
 *
 * 1. Bump generation (cancels any in-flight wait/fade).
 *    - If the previous source was ready → complete forward.
 *    - If not → snap backward (safe, inactive was at 0).
 * 2. Preload + `decode()` the image in the browser cache.
 * 3. `updateImage()` on the inactive Mapbox source.
 * 4. Poll `isSourceLoaded()` via rAF (capped timeout).
 * 5. Once ready:
 *    - High fps: instant swap (inactive→full, active→0, flip).
 *    - Low fps: short rAF fade-in, then hide old + flip.
 */
export function transitionToFrame(
  map: MapboxMap,
  config: TransitionLayerConfig,
  state: TransitionState,
  url: string,
  coordinates: CoordsTuple,
  opacity: number,
  fps: number,
  imageCache: ImageCacheLike,
): void {
  // -- cancel previous transition --
  state.generation++;
  const gen = state.generation;

  if (state.fadeRafId !== null) {
    cancelAnimationFrame(state.fadeRafId);
    if (state.sourceReady) {
      // Source was loaded → complete the old transition forward so the
      // user sees the latest confirmed frame, not a backward jump.
      commitSwap(map, config, state, opacity);
    } else {
      // Source wasn't ready → inactive layer is at 0, snap is invisible.
      snapToSafeState(map, config, state, opacity);
    }
  }
  state.sourceReady = false;

  try {
    if (!map.isStyleLoaded()) return;
  } catch {
    return;
  }

  const slotA = { source: config.sourceIdA, layer: config.layerIdA };
  const slotB = { source: config.sourceIdB, layer: config.layerIdB };
  const active = state.activeSlot === 'A' ? slotA : slotB;
  const inactive = state.activeSlot === 'A' ? slotB : slotA;

  // -- first frame (or after reset): normalize both slots explicitly --
  if (!state.initialized) {
    normalizeSlot(map, active.source, active.layer, url, coordinates, opacity);
    normalizeSlot(map, inactive.source, inactive.layer, url, coordinates, 0);
    state.initialized = true;
    return;
  }

  const { fadeDuration, maxSourceWait } = computeTimingBudget(fps);

  // ----------------------------------------------------------------
  // Phase 2 (after browser pre-decode): hand to Mapbox, wait, fade
  // ----------------------------------------------------------------
  const applyFrame = () => {
    if (state.generation !== gen) return;
    try {
      if (!map.isStyleLoaded()) return;
    } catch {
      return;
    }

    // Update inactive source with the new URL and reset its opacity to 0.
    normalizeSlot(map, inactive.source, inactive.layer, url, coordinates, 0);

    // Move inactive layer above active but below overlays.
    try {
      if (map.getLayer(inactive.layer) && map.getLayer(active.layer)) {
        const anchor = findOverlayAnchor(map, config);
        if (anchor) {
          map.moveLayer(inactive.layer, anchor);
        } else {
          map.moveLayer(inactive.layer);
        }
      }
    } catch {
      // ignore
    }

    // -- poll until Mapbox confirms the source is loaded --
    const waitStart = performance.now();

    const waitForMapbox = () => {
      if (state.generation !== gen) return;

      let loaded = false;
      try {
        loaded = map.isSourceLoaded(inactive.source);
      } catch {
        snapToSafeState(map, config, state, opacity);
        return;
      }

      if (loaded) {
        state.sourceReady = true;
        onSourceReady();
      } else if (performance.now() - waitStart > maxSourceWait) {
        // Timed out -- Mapbox has NOT confirmed the new image.  Drop this
        // frame rather than swapping to an unconfirmed (possibly stale)
        // inactive source.  Reset to safe state and let the next playback
        // tick try the next frame.
        snapToSafeState(map, config, state, opacity);
      } else {
        state.fadeRafId = requestAnimationFrame(waitForMapbox);
      }
    };

    // -- source is ready: instant swap or fade --
    const onSourceReady = () => {
      if (state.generation !== gen) return;

      if (fadeDuration === 0) {
        commitSwap(map, config, state, opacity);
        return;
      }

      // Short fade-in-on-top.
      const fadeStart = performance.now();

      const animate = (now: number) => {
        if (state.generation !== gen) {
          // Cancelled -- newer transition handles state.
          return;
        }

        let styleOk = false;
        try {
          styleOk = map.isStyleLoaded();
        } catch {
          snapToSafeState(map, config, state, opacity);
          return;
        }

        if (!styleOk) {
          state.fadeRafId = requestAnimationFrame(animate);
          return;
        }

        const progress = Math.max(0, Math.min(1, (now - fadeStart) / fadeDuration));

        try {
          if (map.getLayer(inactive.layer)) {
            map.setPaintProperty(
              inactive.layer,
              'raster-opacity',
              opacity * progress,
            );
          }
        } catch {
          snapToSafeState(map, config, state, opacity);
          return;
        }

        if (progress < 1) {
          state.fadeRafId = requestAnimationFrame(animate);
        } else {
          commitSwap(map, config, state, opacity);
        }
      };

      state.fadeRafId = requestAnimationFrame(animate);
    };

    // Kick off the Mapbox-readiness poll.
    state.fadeRafId = requestAnimationFrame(waitForMapbox);
  };

  // ----------------------------------------------------------------
  // Phase 1: pre-decode image in the browser cache
  // ----------------------------------------------------------------
  const cached = imageCache.get(url);
  if (cached && cached.complete && cached.naturalWidth > 0) {
    applyFrame();
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    imageCache.set(url, img);
    if (state.generation !== gen) return;
    if (typeof img.decode === 'function') {
      img.decode()
        .then(() => {
          if (state.generation === gen) applyFrame();
        })
        .catch(() => {
          if (state.generation === gen) applyFrame();
        });
    } else {
      applyFrame();
    }
  };
  img.onerror = () => {
    if (state.generation !== gen) return;
    applyFrame();
  };
  img.src = url;
}

/**
 * Remove both transition layers + sources and reset state.
 */
export function removeTransitionLayers(
  map: MapboxMap,
  config: TransitionLayerConfig,
  state: TransitionState,
): void {
  if (state.fadeRafId !== null) {
    cancelAnimationFrame(state.fadeRafId);
    state.fadeRafId = null;
  }
  state.initialized = false;
  state.activeSlot = 'A';
  state.sourceReady = false;
  try {
    if (map.getLayer(config.layerIdA)) map.removeLayer(config.layerIdA);
    if (map.getSource(config.sourceIdA)) map.removeSource(config.sourceIdA);
    if (map.getLayer(config.layerIdB)) map.removeLayer(config.layerIdB);
    if (map.getSource(config.sourceIdB)) map.removeSource(config.sourceIdB);
  } catch {
    // ignore
  }
}

/**
 * Update the opacity of whichever layer is currently active
 * (for the user-facing opacity slider).
 */
export function updateTransitionOpacity(
  map: MapboxMap,
  config: TransitionLayerConfig,
  state: TransitionState,
  opacity: number,
): void {
  try {
    const activeLayerId =
      state.activeSlot === 'A' ? config.layerIdA : config.layerIdB;
    if (map.getLayer(activeLayerId)) {
      map.setPaintProperty(activeLayerId, 'raster-opacity', opacity);
    }
  } catch {
    // ignore
  }
}
